import React from "react";
import { AlertTriangle, Hand, X, Trash2, Wand2, MapPinned, RefreshCcw } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../ui/select";
import { formatDistanceKm, formatDurationHm } from "../../../lib/utils";

function isValidEndpoint(driver) {
  if (!driver) return false;
  const lat = parseFloat(driver.latitude), lon = parseFloat(driver.longitude);
  return Boolean(driver.latitude && driver.longitude) && !(lat === 0 && lon === 0);
}

export function ActiveRoutePanel({
  route, deliveryIndex, drawActive, onToggleDraw, onSetStartTime, onSetObjective, onSetUseRealDistance,
  onSetServiceTimeMinutes, onSetMaxDistanceKm, onSetMaxDurationHours,
  onRemoveDelivery, onOptimize, onReoptimize, optimizing, onDeleteRoute, outletReady,
  onSaveChanges, onCancelEditing, savingChanges,
}) {
  if (!route) {
    return (
      <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-text-muted" data-testid="no-active-route">
        Click "New Route" to select a driver and start drawing a territory on the map.
      </div>
    );
  }

  const selected = [...route.deliveryIds].map((id) => deliveryIndex.get(id)).filter(Boolean);

  // Editing an already-saved route: simplified panel — add/remove stops only,
  // then either Save Changes (persists) or Cancel Editing (discards, session-only).
  if (route.existingRouteId) {
    const original = route.originalDeliveryIds || new Set();
    const added = [...route.deliveryIds].filter((id) => !original.has(id)).length;
    const removed = [...original].filter((id) => !route.deliveryIds.has(id)).length;
    const hasChanges = added > 0 || removed > 0;
    return (
      <div className="rounded-md border border-accent bg-surface p-4" data-testid={`active-route-panel-existing-${route.existingRouteId}`}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ background: route.color }} />
            <p className="font-heading text-sm font-semibold text-text-main">Editing Route #{route.existingRouteId} — {route.driverName}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onDeleteRoute} data-testid={`delete-existing-route-${route.existingRouteId}`}>
            <Trash2 size={15} strokeWidth={1.5} className="text-danger" />
          </Button>
        </div>

        <Button
          variant={drawActive ? "accent" : "outline"} className="mb-3 w-full" onClick={onToggleDraw}
          data-testid={`select-area-button-${route.tempId}`}
        >
          <Hand size={15} strokeWidth={1.5} /> {drawActive ? "Drawing... (click points, double-click to finish)" : "Select Area to Add Stops"}
        </Button>

        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
          Stops ({selected.length}){hasChanges && <span className="text-accent"> · {added} added, {removed} removed</span>}
        </p>
        <div className="mb-3 max-h-64 space-y-1 overflow-y-auto rounded-md border border-border bg-background p-2" data-testid={`selected-stops-list-${route.tempId}`}>
          {selected.length === 0 && <p className="p-2 text-xs text-text-muted">No stops on this route. Draw a polygon or click an unassigned marker on the map.</p>}
          {selected.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2 rounded-sm bg-surface px-2 py-1 text-xs" data-testid={`selected-stop-${d.id}`}>
              <span className="truncate">{d.customer_name || `#${d.id}`} — {d.address}</span>
              <button onClick={() => onRemoveDelivery(d.id)} data-testid={`remove-stop-${d.id}`}>
                <X size={13} strokeWidth={2} className="text-danger" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onCancelEditing} data-testid={`cancel-editing-button-${route.tempId}`}>
            Cancel Editing
          </Button>
          <Button
            variant="accent" className="flex-1" disabled={!hasChanges || savingChanges}
            onClick={onSaveChanges} data-testid={`save-changes-button-${route.tempId}`}
          >
            {savingChanges ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    );
  }

  const endpointValid = isValidEndpoint(route.driverEndpoint);
  const canOptimize = outletReady && endpointValid && selected.length > 0;

  return (
    <div className="rounded-md border border-border bg-surface p-4" data-testid={`active-route-panel-${route.tempId}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full" style={{ background: route.color }} />
          <p className="font-heading text-sm font-semibold text-text-main">Route {route.tempId} — {route.driverName}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onDeleteRoute} data-testid={`delete-route-${route.tempId}`}>
          <Trash2 size={15} strokeWidth={1.5} className="text-danger" />
        </Button>
      </div>

      {!endpointValid && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-warning/40 bg-warning-bg px-3 py-2 text-xs text-warning" data-testid={`endpoint-warning-${route.tempId}`}>
          <AlertTriangle size={14} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
          This driver has no valid endpoint (lat/long) set. Add it on the Drivers page before optimizing this route.
        </div>
      )}

      <div className="mb-3 grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Start Time</Label>
          <Input type="time" value={route.startTime} onChange={(e) => onSetStartTime(e.target.value)} data-testid={`start-time-${route.tempId}`} />
        </div>
        <div>
          <Label className="text-xs">Optimize For</Label>
          <Select value={route.objective} onValueChange={onSetObjective}>
            <SelectTrigger data-testid={`objective-select-${route.tempId}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="time">Fastest Completion Time</SelectItem>
              <SelectItem value="distance">Shortest Distance</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Time per Stop (min)</Label>
          <Input
            type="number" min="0" max="60" step="0.5" value={route.serviceTimeMinutes}
            onChange={(e) => onSetServiceTimeMinutes(Math.max(0, Math.min(60, Number(e.target.value))))}
            data-testid={`service-time-input-${route.tempId}`}
          />
        </div>
      </div>

      <label className="mb-3 flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs cursor-pointer" data-testid={`use-real-distance-toggle-${route.tempId}`}>
        <input
          type="checkbox" checked={route.useRealDistance}
          onChange={(e) => onSetUseRealDistance(e.target.checked)}
          className="h-3.5 w-3.5 accent-primary"
        />
        <MapPinned size={14} strokeWidth={1.5} className="text-text-muted" />
        <span className="text-text-main">Use real road distance (ORS)</span>
        <span className="text-text-muted">— accounts for rivers/bridges, falls back to straight-line if unavailable</span>
      </label>

      <div className="mb-3 grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Max Distance (km, optional)</Label>
          <Input
            type="number" min="0" step="1" placeholder="No limit" value={route.maxDistanceKm}
            onChange={(e) => onSetMaxDistanceKm(e.target.value)}
            data-testid={`max-distance-input-${route.tempId}`}
          />
        </div>
        <div>
          <Label className="text-xs">Max Time (hours, optional)</Label>
          <Input
            type="number" min="0" step="0.5" placeholder="No limit" value={route.maxDurationHours}
            onChange={(e) => onSetMaxDurationHours(e.target.value)}
            data-testid={`max-duration-input-${route.tempId}`}
          />
        </div>
      </div>
      {(route.maxDistanceKm || route.maxDurationHours) && (
        <p className="mb-3 text-xs text-text-muted">
          Stops that don't fit within the cap will be excluded and shown below as "left over" — move them to another route.
        </p>
      )}

      <Button
        variant={drawActive ? "accent" : "outline"} className="mb-3 w-full" onClick={onToggleDraw}
        data-testid={`select-area-button-${route.tempId}`}
      >
        <Hand size={15} strokeWidth={1.5} /> {drawActive ? "Drawing... (click points, double-click to finish)" : "Select Area"}
      </Button>

      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
        Selected Stops ({selected.length})
      </p>
      <div className="mb-3 max-h-48 space-y-1 overflow-y-auto rounded-md border border-border bg-background p-2" data-testid={`selected-stops-list-${route.tempId}`}>
        {selected.length === 0 && <p className="p-2 text-xs text-text-muted">No stops selected yet. Draw a polygon or click a marker on the map.</p>}
        {selected.map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-2 rounded-sm bg-surface px-2 py-1 text-xs" data-testid={`selected-stop-${d.id}`}>
            <span className="truncate">{d.customer_name || `#${d.id}`} — {d.address}</span>
            <button onClick={() => onRemoveDelivery(d.id)} data-testid={`remove-stop-${d.id}`}>
              <X size={13} strokeWidth={2} className="text-danger" />
            </button>
          </div>
        ))}
      </div>

      <Button
        variant="accent" className="w-full" disabled={!canOptimize || optimizing}
        onClick={onOptimize} data-testid={`optimize-route-button-${route.tempId}`}
      >
        <Wand2 size={15} strokeWidth={1.5} /> {optimizing ? "Optimizing..." : "Optimize Route"}
      </Button>

      {route.optimized && (
        <Button
          variant="outline" className="mt-2 w-full" disabled={optimizing}
          onClick={onReoptimize} data-testid={`reoptimize-route-button-${route.tempId}`}
        >
          <RefreshCcw size={15} strokeWidth={1.5} /> Re-optimize (try a different sequence)
        </Button>
      )}

      {route.optimized && (
        <div className="mt-3 rounded-md border border-border bg-background p-3 text-xs" data-testid={`optimized-result-${route.tempId}`}>
          <p className="font-semibold text-text-main">
            Optimizer mode: {route.optimized.mode?.toUpperCase()} · Goal: {route.objective === "distance" ? "Shortest Distance" : "Fastest Time"} · Variant {route.variant || 1} of 6
          </p>
          <p className={`mt-1 font-medium ${(route.optimized.distanceSource === "ors_matrix" || route.optimized.distanceSource === "osrm_matrix") ? "text-success" : "text-warning"}`}>
            {(route.optimized.distanceSource === "ors_matrix" || route.optimized.distanceSource === "osrm_matrix") ? "Real road distance" : "Straight-line estimate (haversine)"}
          </p>
          <p className="mt-1 text-text-muted">
            {selected.length - (route.optimized.unassigned?.length || 0)} of {selected.length} stops · {formatDistanceKm(route.optimized.distance)} · {formatDurationHm(route.optimized.duration)} est. (incl. {route.serviceTimeMinutes} min/stop service time)
          </p>
          {route.optimized.unassigned?.length > 0 && (
            <div className="mt-2 rounded-md border border-danger/40 bg-danger/5 p-2" data-testid={`left-over-stops-${route.tempId}`}>
              <p className="font-medium text-danger">
                {route.optimized.unassigned.length} stop(s) left over{(route.maxDistanceKm || route.maxDurationHours) ? " (exceeded your max distance/time cap)" : ""} — move them to another route:
              </p>
              <ul className="mt-1 max-h-24 list-disc space-y-0.5 overflow-y-auto pl-4 text-text-muted">
                {route.optimized.unassigned.map((u) => {
                  const d = deliveryIndex.get(u.id);
                  return <li key={u.id}>{d?.customer_name || `Delivery #${u.id}`}</li>;
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
